import React, { useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Upload, X, Loader2, GripVertical } from "lucide-react";

export const uploadedImageUrl = (path) => `${API}/images/${path}`;

export function ImageUploader({ value = [], onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(0);
  const [dragIdx, setDragIdx] = useState(null);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(list.length);
    const added = [];
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/admin/uploads", fd);
        added.push(data.path);
      } catch (err) {
        toast.error(`${f.name}: ${formatApiErrorDetail(err.response?.data?.detail)}`);
      }
      setUploading((n) => n - 1);
    }
    if (added.length) onChange([...value, ...added]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const move = (from, to) => {
    if (from === null || from === to) return;
    const next = [...value];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  return (
    <div data-testid="image-uploader">
      <div className="flex flex-wrap gap-3">
        {value.map((p, i) => (
          <div
            key={p}
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { move(dragIdx, i); setDragIdx(null); }}
            className={`relative h-24 w-24 rounded-lg border bg-white overflow-hidden cursor-move transition-opacity ${dragIdx === i ? "opacity-40" : ""} ${i === 0 ? "ring-2 ring-brand-navy" : ""}`}
            data-testid={`uploaded-image-${i}`}
          >
            <img src={uploadedImageUrl(p)} alt="" className="h-full w-full object-contain" />
            <GripVertical className="absolute left-1 top-1 h-4 w-4 text-brand-navy/60" />
            {i === 0 && <span className="absolute bottom-0 inset-x-0 bg-brand-navy text-white text-[10px] text-center py-0.5">Main</span>}
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))}
              className="absolute right-1 top-1 rounded-full bg-white/90 p-0.5 hover:bg-red-100" data-testid={`remove-image-${i}`}>
              <X className="h-3.5 w-3.5 text-red-600" />
            </button>
          </div>
        ))}
        <Button type="button" variant="outline" disabled={uploading > 0} onClick={() => inputRef.current?.click()}
          className="h-24 w-24 flex-col gap-1 border-dashed" data-testid="upload-image-btn">
          {uploading > 0 ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
          <span className="text-xs">{uploading > 0 ? `Uploading ${uploading}` : "Add images"}</span>
        </Button>
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple hidden
        onChange={(e) => handleFiles(e.target.files)} data-testid="upload-image-input" />
      <p className="mt-2 text-xs text-muted-foreground">First image is the main photo. Drag to reorder. JPG/PNG/WEBP, max 10MB. Uploaded images replace the built-in photos.</p>
    </div>
  );
}
